import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { API_URL } from '../shared/api/base';
import { LsKeys } from '../enums';

export const authServiceClient = axios.create({
  baseURL: API_URL,
  timeout: 30 * 1000,
  headers: {
    'Content-Type': 'application/json',
  },
});

type StoredTokens = { accessToken?: string; refreshToken?: string };

/**
 * Reads the persisted token pair.
 *
 * The previous implementation was `JSON.parse(localStorage.getItem(KEY)!)`.
 * The non-null assertion is a lie whenever the key is absent, and any garbage
 * left in the slot (a half-written value, or a string from an older build)
 * makes JSON.parse throw — inside a request interceptor, which turns every
 * subsequent API call into an unhandled rejection.
 */
export const readStoredTokens = (): StoredTokens => {
  try {
    const raw = localStorage.getItem(LsKeys.TOKEN_INFO);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    localStorage.removeItem(LsKeys.TOKEN_INFO);
    return {};
  }
};

const writeStoredTokens = (tokens: StoredTokens) => {
  localStorage.setItem(LsKeys.TOKEN_INFO, JSON.stringify(tokens));
};

/**
 * Endpoints that must never trigger a refresh-and-retry: a 401 from any of them
 * means the supplied credentials are wrong, not that the session aged out.
 * Retrying would loop.
 */
const AUTH_PATHS = [
  '/auth/sign-in-admin',
  '/auth/sign-in',
  '/auth/sign-up',
  '/auth/refresh-token',
  '/auth/restore-password',
  '/auth/verify-reset',
  // A 401 here means the RESET token is bad or already spent, not that a
  // session aged out — there is no session during a password reset. Without
  // this entry the interceptor tried to refresh, found no refresh token, and
  // forceLogout() threw the admin back to /login mid-flow instead of showing
  // them why the reset failed.
  '/auth/reset-password',
];

const isAuthPath = (url?: string) => !!url && AUTH_PATHS.some(p => url.includes(p));

authServiceClient.interceptors.request.use(
  config => {
    const { accessToken } = readStoredTokens();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  error => Promise.reject(error),
);

const forceLogout = () => {
  localStorage.removeItem(LsKeys.TOKEN_INFO);
  // A hard navigation also resets the Redux store, which avoids importing it
  // here — config -> state -> thunks -> api -> config is a cycle.
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

/**
 * Access tokens live 15 minutes (JWT_ACCESS_EXPIRATION_TIME), refresh tokens 7
 * days. Without this interceptor the admin silently breaks a quarter of an hour
 * after login: every request 401s, thunks reject, and pages render empty with
 * no indication that the session simply expired.
 *
 * Concurrent 401s share a single refresh call — otherwise a dashboard that
 * fires six requests at once would trigger six refreshes and race each other's
 * token writes.
 */
let refreshInFlight: Promise<string> | null = null;

const refreshAccessToken = async (): Promise<string> => {
  const { refreshToken } = readStoredTokens();
  if (!refreshToken) throw new Error('No refresh token');

  // Deliberately a bare axios call: using authServiceClient would re-enter this
  // interceptor, and importing the api layer would close the module cycle.
  const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
    `${API_URL}/auth/refresh-token`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' }, timeout: 30 * 1000 },
  );

  writeStoredTokens({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken ?? refreshToken,
  });

  return data.accessToken;
};

authServiceClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    const shouldRefresh =
      error.response?.status === 401 &&
      !!original &&
      !original._retry &&
      !isAuthPath(original.url);

    if (!shouldRefresh) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      if (!refreshInFlight) {
        refreshInFlight = refreshAccessToken().finally(() => {
          refreshInFlight = null;
        });
      }

      const accessToken = await refreshInFlight;

      original.headers = { ...original.headers, Authorization: `Bearer ${accessToken}` };

      return authServiceClient(original);
    } catch {
      // The refresh token is gone or expired too — the session is genuinely over.
      forceLogout();
      return Promise.reject(error);
    }
  },
);

export const extractErrorMessage = (e: Error | AxiosError | unknown): string | null => {
  if (axios.isCancel(e)) {
    return null;
  }
  if (axios.isAxiosError(e)) {
    const statusCode = e.response?.status;

    if (statusCode) {
      if (e.response?.data?.message === 'Wrong refresh token') {
        return null;
      }
      if (e.response?.data?.error) {
        return e.response?.data?.error;
      }
    }
  } else if (e instanceof Error && e?.message) {
    return e.message;
  }

  return 'Something went wrong';
};
